import
{
  FC,
  PropsWithChildren
} from "react";

import
{
  useL10n
} from "@ews/react-localization-context";

import
{
  MenuLink,
  MenuSpacer,
  Sidebar,
  SidebarSection
} from "./Sidebar";

import
{
  RouteParams,
  useNamedRoutes
} from "../../NamedRoutes";

import type {
  MenuToggleProps
} from "../types";

import
{
  AcquireAuthorization
} from "./Main";

import
{
  SystemType,
  UserPermissions,
  permissionAllowed
} from "@ews/react-data";

import
{
  useDoubleConfirmDialog
} from "../../Dialog";

import
{
  useMyPermissions
} from "../../Login/LoginProvider";

import
{
  useRemoveSystem,
  useSystem
} from "../../ReactData/system";

import
{
  ReactClickEvent
} from "../../types";

import Icon from "@ews/react-icons";
import Feature from "../../Feature";

type SidebarProps = MenuToggleProps & {
  routeParams?: RouteParams;
  title?: string;
};

export const hasPermissionMeasurements = (
  permissions: UserPermissions
) =>
{

  if (permissionAllowed(permissions.permitAccessLevel3!)) return true;
  if (Boolean(permissions.detectorValuesAccessLevel2) && permissionAllowed(permissions.permitAccessLevel2!)) return true;

  return false;

};

const System: FC<SidebarProps & PropsWithChildren> = ({
  routeParams = {},
  onMenuToggle,
  title,
  children,
}) =>
{
  const { translate: t } = useL10n();
  const { generate, params, router } = useNamedRoutes();
  const systemId = routeParams.id || params().id;
  const permissions: UserPermissions = useMyPermissions();
  const removeSystem = useRemoveSystem();
  const dialog = useDoubleConfirmDialog();
  const { systemType } = useSystem(systemId || "");

  if (!("permitAccessLevel3" in permissions)) return <></>;

  let isAllowedMeasurements = hasPermissionMeasurements(
    permissions
  );

  //title = title || t("[REACT_SYSTEMSETTINGS]");

  const handleDelete = async (e: ReactClickEvent) =>
  {
    //const systemId = routeParams.id || params().id;
    if (!systemId) return;

    const response = await dialog({
      title: t("[REACT_REMOVE_SYSTEM]"),
      message: t("[CONFIRM_REMOVE_SYSTEM]"),
      areYouSureButtonTitle: t("COM_TEMPLATES_ARE_YOU_SURE"),
      confirmButtonTitle: t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK"),
      cancelButtonTitle: t("COM_BANNERS_CANCEL"),
    });

    if (response) {
      await removeSystem(systemId);
      onMenuToggle && onMenuToggle(e);
      const redirect = generate("systems");
      router?.push(redirect);
    }
  };

  return (
    <Sidebar title={title} onMenuToggle={onMenuToggle}>
      <SidebarSection grow>
        <AcquireAuthorization
          systemId={params().id || ""}
          onMenuToggle={onMenuToggle}
        />
        {children}
        <MenuSpacer />
        <>
          <MenuLink
            title={t("[SID_MNU_TAB_SETTINGS]")}
            onClick={onMenuToggle}
            link={generate("system:settings", routeParams)}
            icon={<Icon icon="settings" />}
          />
          <MenuSpacer />
        </>
        {permissionAllowed(permissions.permitAccessLevel3!) && (
          <MenuLink
            title={t("COM_FACPMANAGEMENT_FACP_TESTCONDITIONS")} //[SID_SUB_MNU_STA_SERVICE_LIST] ?
            onClick={onMenuToggle}
            link={generate("system:maintenancereport", routeParams)}
            icon={<Icon icon="maint_report" />}
          />
        )}
        <MenuLink
          title={t("[SID_MNU_TAB_EVENT_MEM]")}
          onClick={onMenuToggle}
          link={generate("system:eventmemory", routeParams)}
          icon={<Icon icon="event_memory" />}
        />
        {isAllowedMeasurements && (
          <MenuLink
            title={t("COM_FACPMANAGEMENT_FACP_MEASUREMENTS")}
            onClick={onMenuToggle}
            link={generate("system:analoguevalues", routeParams)}
            icon={<Icon icon="meas_value_report" />}
          />
        )}
        {/* <MenuLink
                    title={t('COM_FACPMANAGEMENT_FACP_QUERY')}
                    onClick={onMenuToggle}
                    link={generate('system:queries', routeParams)}
                    icon={swapVerticalOutline}
                /> */}
        <MenuLink
          title={t("COM_FACPMANAGEMENT_SHARE_SHOW_DETAILS")}
          onClick={onMenuToggle}
          link={generate("system:shares", routeParams)}
          icon={<Icon icon="enablement" />}
        />
        <MenuLink
          title={t("[REACT_SHOW_PHONENUMBERS]")}
          onClick={onMenuToggle}
          link={generate("system:phonenumber", routeParams)}
          icon={<Icon icon="phone_numb" />}
        />
        <MenuLink
          title={t("[REACT_SEND_TEXT_LOGS]")}
          link={generate("system:sent-text-messages-log", routeParams)}
          onClick={onMenuToggle}
          icon={<Icon icon="list" />}
        />
        {(permissionAllowed(permissions.permitAccessLevel3!) && systemType === SystemType.BC600) && <Feature feature="TRVB">
          <MenuSpacer />
          <MenuLink
            title={t("[REACT_SYSTEM_DOCUMENTATION]")}
            link={generate("system:documentation", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="system_documentation" />}
          />
          <MenuLink
            title={t("[REACT_DETAILS_ON_SUBMISSION_DOCUMENTS]")}
            link={generate("system:submissiondocuments", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="details_on_submission_documents" />}
          />
          <MenuLink
            title={t("[REACT_POWER_PACKS_FOR_SUBMISSION_DOCUMENTS]")}
            link={generate("system:powerunitsDocumentation", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="power_packs_for_submission_documents" />}
          />
          <MenuLink
            title={t('[REACT_DISPLAY_DEVICES]')}
            link={generate('system:displaydevice', routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="show_devices" />}
          />
          <MenuLink
            title={t("[REACT_OTHER_ALARM_DEVICES]")}
            link={generate("system:otheralarmdevices", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="other_alarm_devices" />}
          />
          <MenuLink
            title={t("[REACT_OTHER_DEVICES]")}
            link={generate("system:otherdevices", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="other_devices" />}
          />
          <MenuLink
            title={t("[REACT_INPUTS_SUBMISSION_DOCUMENTS]")}
            link={generate("system:typedefinition-submission-documents", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="inputs_for_submission_documents" />}
          />
          <MenuLink
            title={t("[REACT_TYPEDEFINITION_SUBMISSION_DOCUMENTS]")}
            link={generate("system:elementslist", routeParams)}
            onClick={onMenuToggle}
            icon={<Icon icon="type_definition_submission_documents" />}
          />
        </Feature>}
        {permissionAllowed(permissions.grantSystemAccess!) ? (
          <>
            <MenuSpacer />
            <MenuLink
              title={t("[SID_MNU_HK_DELETE]")}
              onClick={handleDelete}
              icon={<Icon icon="delete" />}
            />
          </>
        ) : (
          <></>
        )}
      </SidebarSection>
    </Sidebar >
  );
};

export default System;
